import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import { Breadcrumb } from '@/components/base/Breadcrumb';
import { categories, getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const cat = article ? categories.find((c) => c.slug === article.category) : undefined;
  const related = article ? getRelatedArticles(article, 4) : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex flex-col items-center justify-center px-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 mb-4">
            <i className="ri-error-warning-line text-2xl text-foreground-300"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-950">文章未找到</h2>
          <p className="text-foreground-500 mt-2">该文章可能已被删除或地址有误</p>
          <Link to="/" className="mt-4 px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '黑料曝光',
      logo: {
        '@type': 'ImageObject',
        url: 'https://readdy.ai/api/search-image?query=A%20bold%20journalism%20logo%20with%20detective%20magnifying%20glass%20and%20dramatic%20lighting%2C%20minimal%20design%2C%20dark%20and%20warm%20tones&width=512&height=512&seq=logo-squared&orientation=squarish',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': typeof window !== 'undefined' ? window.location.href : '',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin + '/' : '' },
      {
        '@type': 'ListItem',
        position: 2,
        name: cat?.name || '分类',
        item: typeof window !== 'undefined' ? window.location.origin + `/category/${article.category}` : '',
      },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        ogType="article"
        ogImage={article.coverImage}
        canonicalUrl={typeof window !== 'undefined' ? window.location.href : ''}
        schema={[articleSchema, breadcrumbSchema]}
      />
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-6 md:px-8 py-8">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: cat?.name || '分类', href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <header className="mt-6">
              <div className="flex items-center gap-3 flex-wrap mb-4">
                {cat && (
                  <Link to={`/category/${article.category}`} className="px-2.5 py-1 text-xs font-medium rounded-md bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors whitespace-nowrap">
                    {cat.name}
                  </Link>
                )}
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs text-foreground-400 whitespace-nowrap">#{tag}</span>
                ))}
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>

              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-400 flex-wrap">
                <span className="font-medium text-foreground-700">{article.author}</span>
                <span className="whitespace-nowrap">{article.publishedAt}</span>
                <span className="whitespace-nowrap">{article.readCount.toLocaleString()} 阅读</span>
              </div>
            </header>

            <div className="mt-8 rounded-xl overflow-hidden aspect-[16/9]">
              <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover" />
            </div>

            <div className="mt-8 prose-sm max-w-none">
              {article.content.split('\n\n').map((paragraph, i) => (
                <p key={i} className="text-base leading-relaxed text-foreground-700 mb-5">
                  {paragraph.trim()}
                </p>
              ))}
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="w-full px-6 md:px-8 py-10 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {related.map((rel) => (
                  <ArticleCard
                    key={rel.id}
                    slug={rel.slug}
                    title={rel.title}
                    excerpt={rel.excerpt}
                    coverImage={rel.coverImage}
                    category={rel.category}
                    author={rel.author}
                    publishedAt={rel.publishedAt}
                    readCount={rel.readCount}
                    tags={rel.tags}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}